import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock,
  Send,
  MessageSquare,
  Building2,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { contentService, complaintService } from '@/services/api';
import { getMailHref, getMapHref, getTelHref } from '@/lib/contactLinks';

const normalizeLanguage = (language) =>
  String(language || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';

const knownFaqTranslations = {
  'How do I book an appointment?': {
    ar: 'كيف يمكنني حجز موعد؟',
    en: 'How do I book an appointment?',
  },
  'How can I update my medical history?': {
    ar: 'كيف يمكنني تحديث سجلي الطبي؟',
    en: 'How can I update my medical history?',
  },
  'Open doctors page, choose a doctor, and confirm your slot.': {
    ar: 'افتح صفحة الأطباء، اختر طبيبًا، ثم أكد الموعد المناسب لك.',
    en: 'Open doctors page, choose a doctor, and confirm your slot.',
  },
  'Go to medical records and save your latest details.': {
    ar: 'انتقل إلى السجلات الطبية واحفظ آخر تفاصيلك.',
    en: 'Go to medical records and save your latest details.',
  },
};

const readLocalizedValue = (value, lang) => {
  if (value == null || value === false) return '';

  if (typeof value === 'object' && !Array.isArray(value)) {
    const preferredKeys =
      lang === 'ar'
        ? ['ar', 'ar_SY', 'ar-SY', 'arabic', 'value', 'name', 'en', 'en_US', 'en-US', 'english']
        : ['en', 'en_US', 'en-US', 'english', 'value', 'name', 'ar', 'ar_SY', 'ar-SY', 'arabic'];

    for (const key of preferredKeys) {
      const resolved = readLocalizedValue(value[key], lang);
      if (resolved) return resolved;
    }
    return '';
  }

  return String(value).trim();
};

const translateKnownFaqText = (value, lang) => {
  const text = String(value || '').trim();
  return knownFaqTranslations[text]?.[lang] || text;
};

const readFaqField = (faq, field, lang) => {
  const odooLang = lang === 'ar' ? 'ar_SY' : 'en_US';
  const translatedField = field === 'question' ? 'title' : 'description';
  const translations = faq?.translations || {};
  const candidates = [
    translations?.[lang]?.[field],
    translations?.[odooLang]?.[field],
    faq?.[`${field}_${lang}`],
    faq?.[`${field}_${odooLang}`],
    faq?.[`${field}_${lang === 'ar' ? 'arabic' : 'english'}`],
    faq?.[field],
    faq?.[translatedField],
  ];

  for (const candidate of candidates) {
    const value = readLocalizedValue(candidate, lang);
    if (value) {
      return translateKnownFaqText(value, lang);
    }
  }

  return '';
};

const localizeFaq = (faq, lang) => ({
  q: readFaqField(faq, 'question', lang),
  a: readFaqField(faq, 'answer', lang),
});

const ContactPage = () => {
  const { i18n } = useTranslation();
  const lang = normalizeLanguage(i18n.resolvedLanguage || i18n.language || 'ar');
  const isRTL = lang === 'ar';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [faqs, setFaqs] = useState([]);

  // Fetch settings and FAQs whenever the active language changes.
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch settings
        const settingsResponse = await contentService.getSettings();
        if (settingsResponse.data?.data) {
          setSettings(settingsResponse.data.data);
        }
        
        // Fetch FAQs
        try {
          const faqsResponse = await contentService.getFaqs();
          if (faqsResponse.data?.data && Array.isArray(faqsResponse.data.data)) {
            setFaqs(faqsResponse.data.data.slice(0, 5)); // Get first 5 FAQs
          }
        } catch (faqErr) {
          console.log('FAQs not available, using defaults');
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [lang]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const body = [
        formData.message?.trim() || '',
        formData.name?.trim() ? 'Name: ' + formData.name.trim() : '',
        formData.email?.trim() ? 'Email: ' + formData.email.trim() : '',
        formData.phone?.trim() ? 'Phone: ' + formData.phone.trim() : '',
        formData.subject?.trim() ? 'Subject: ' + formData.subject.trim() : '',
      ]
        .filter(Boolean)
        .join('\n');

      await complaintService.create({
        title: formData.subject?.trim()
          ? 'Contact Request - ' + formData.subject.trim()
          : 'Contact Request',
        body,
        message: body,
      });

      toast.success(lang === 'ar' ? 'تم إرسال رسالتك بنجاح' : 'Your message has been sent successfully!');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      const apiMessage = error?.response?.data?.error || error?.response?.data?.message;
      toast.error(apiMessage || (lang === 'ar' ? 'تعذر إرسال رسالتك' : 'Failed to send your message'));
    } finally {
      setSubmitting(false);
    }
  };

  // Default contact info (can be overridden by settings)
  const contactMapAddress = 'Al-Hamra Street, Building 15, Damascus, Syria';
  const contactMapHref = getMapHref(contactMapAddress);
  const contactInfo = [
    {
      icon: Phone,
      title: lang === 'ar' ? 'اتصل بنا' : 'Call Us',
      details: ['+963 11 123 4567', '+963 933 123 456'].map((phone) => ({
        label: phone,
        href: getTelHref(phone),
        dir: 'ltr',
        ariaLabel: lang === 'ar' ? `الاتصال على ${phone}` : `Call ${phone}`,
      })),
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
    },
    {
      icon: Mail,
      title: lang === 'ar' ? 'راسلنا عبر البريد' : 'Email Us',
      details: ['contact@dactrha.com', 'support@dactrha.com'].map((email) => ({
        label: email,
        href: getMailHref(email),
        dir: 'ltr',
        ariaLabel: lang === 'ar' ? `إرسال بريد إلى ${email}` : `Email ${email}`,
      })),
      color: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400'
    },
    {
      icon: MapPin,
      title: lang === 'ar' ? 'موقعنا' : 'Our Location',
      details: [
        lang === 'ar' ? 'دمشق، سوريا' : 'Damascus, Syria',
        lang === 'ar' ? 'شارع الحمراء، بناء رقم 15' : 'Al-Hamra Street, Building 15',
      ].map((location) => ({
        label: location,
        href: contactMapHref,
        external: true,
        ariaLabel: lang === 'ar' ? `فتح الموقع على الخريطة: ${location}` : `Open location on map: ${location}`,
      })),
      color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
    },
    {
      icon: Clock,
      title: lang === 'ar' ? 'ساعات العمل' : 'Working Hours',
      details: [
        lang === 'ar' ? 'الأحد - الخميس: 9 صباحًا - 6 مساءً' : 'Sun - Thu: 9 AM - 6 PM',
        lang === 'ar' ? 'الجمعة - السبت: مغلق' : 'Fri - Sat: Closed',
      ].map((label) => ({ label })),
      color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400'
    },
  ];

  // Default FAQs if API doesn't return any
  const defaultFaqs = [
    {
      q: lang === 'ar' ? 'كيف يمكنني حجز موعد؟' : 'How can I book an appointment?',
      a: lang === 'ar' ? 'يمكنك حجز موعد بسهولة من خلال البحث عن الطبيب المناسب ثم اختيار الوقت الذي يناسبك.' : 'You can easily book an appointment by searching for a doctor and selecting a suitable time'
    },
    {
      q: lang === 'ar' ? 'هل الاستشارات عبر الإنترنت متاحة؟' : 'Are online consultations available?',
      a: lang === 'ar' ? 'نعم، نوفر استشارات طبية عبر الفيديو مع أطباء معتمدين.' : 'Yes, we provide video medical consultations with certified doctors'
    },
    {
      q: lang === 'ar' ? 'كيف يمكنني إلغاء أو تعديل موعدي؟' : 'How can I cancel or change my appointment?',
      a: lang === 'ar' ? 'يمكنك إدارة مواعيدك من خلال قسم "مواعيدي" داخل حسابك.' : 'You can manage your appointments through the "My Appointments" section in your account'
    },
  ];

  const displayFaqs = faqs.length > 0
    ? faqs.map((faq) => localizeFaq(faq, lang))
    : defaultFaqs;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" dir={isRTL ? 'rtl' : 'ltr'} data-testid="contact-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-white/20 text-white mb-4">
            {lang === 'ar' ? 'تواصل معنا' : 'Contact Us'}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {lang === 'ar' ? 'نحن هنا لمساعدتك' : "We're Here to Help"}
          </h1>
          <p className="text-xl text-teal-100 max-w-2xl mx-auto">
            {lang === 'ar' 
              ? 'لديك سؤال أو استفسار؟ فريقنا جاهز لمساعدتك والإجابة عن جميع استفساراتك.'
              : 'Have a question or inquiry? Our team is ready to help you 24/7'}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Contact Info Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 -mt-16 mb-12">
          {loading ? (
            Array.from({ length: 4 }).map((_, idx) => (
              <Card key={idx} className="dark:bg-slate-800 border-0 shadow-lg">
                <CardContent className="p-6">
                  <Skeleton className="w-12 h-12 rounded-full mb-4" />
                  <Skeleton className="h-5 w-1/2 mb-2" />
                  <Skeleton className="h-4 w-3/4 mb-1" />
                  <Skeleton className="h-4 w-2/3" />
                </CardContent>
              </Card>
            ))
          ) : (
            contactInfo.map((info, idx) => (
              <Card key={idx} className="dark:bg-slate-800 border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-full ${info.color} flex items-center justify-center mb-4`}>
                    <info.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">{info.title}</h3>
                  {info.details.map((detail, i) => {
                    const content = detail.dir ? <span dir={detail.dir}>{detail.label}</span> : detail.label;

                    return detail.href ? (
                      <a
                        key={i}
                        href={detail.href}
                        target={detail.external ? '_blank' : undefined}
                        rel={detail.external ? 'noopener noreferrer' : undefined}
                        aria-label={detail.ariaLabel}
                        className="block text-sm text-slate-600 transition-colors hover:text-teal-600 hover:underline dark:text-slate-400 dark:hover:text-teal-400"
                      >
                        {content}
                      </a>
                    ) : (
                      <p key={i} className="text-sm text-slate-600 dark:text-slate-400">{content}</p>
                    );
                  })}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="dark:bg-slate-800 border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-teal-600" />
                {lang === 'ar' ? 'أرسل لنا رسالة' : 'Send Us a Message'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{lang === 'ar' ? 'الاسم الكامل' : 'Full Name'}</Label>
                    <Input
                      id="name"
                      data-testid="contact-name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder={lang === 'ar' ? 'أدخل اسمك الكامل' : 'Enter your name'}
                      required
                      className="dark:bg-slate-900"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">{lang === 'ar' ? 'البريد الإلكتروني' : 'Email'}</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder={lang === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                      required
                      className="dark:bg-slate-900"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">{lang === 'ar' ? 'رقم الهاتف' : 'Phone Number'}</Label>
                    <Input
                      id="phone"
                      data-testid="contact-phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder={lang === 'ar' ? 'أدخل رقم هاتفك' : 'Enter your phone'}
                      className="dark:bg-slate-900"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">{lang === 'ar' ? 'الموضوع' : 'Subject'}</Label>
                    <Input
                      id="subject"
                      data-testid="contact-subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      placeholder={lang === 'ar' ? 'موضوع الرسالة' : 'Message subject'}
                      required
                      className="dark:bg-slate-900"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">{lang === 'ar' ? 'الرسالة' : 'Message'}</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder={lang === 'ar' ? 'اكتب رسالتك هنا...' : 'Write your message here...'}
                    rows={5}
                    required
                    className="dark:bg-slate-900"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-teal-600 hover:bg-teal-700" 
                  disabled={submitting}
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                      {lang === 'ar' ? 'جارٍ الإرسال...' : 'Sending...'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      {lang === 'ar' ? 'إرسال الرسالة' : 'Send Message'}
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* FAQ Section */}
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
              {lang === 'ar' ? 'الأسئلة الشائعة' : 'Frequently Asked Questions'}
            </h2>
            <div className="space-y-4">
              {displayFaqs.map((faq, idx) => (
                <Card key={idx} className="dark:bg-slate-800 border-0 shadow-sm">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-2">{faq.q}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{faq.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;

